import { useEffect, useRef, useState } from "react";
import { api, API, getToken, fileUrl } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Palette, Upload, Send, Check, AlertCircle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";
import { useAuth } from "../lib/auth";

const COLUMNS = [
  { id: "borrador", label: "Solicitudes nuevas" },
  { id: "brief_incompleto", label: "Brief incompleto" },
  { id: "en_espera", label: "En espera" },
  { id: "en_produccion", label: "En diseño" },
  { id: "en_revision", label: "En revisión" },
  { id: "requiere_cambios", label: "Cambios" },
  { id: "aprobado", label: "Aprobado" },
  { id: "entregado", label: "Entregado" },
  { id: "publicado", label: "Publicado" },
];

const PRIORITY_COLORS = {
  baja: "pill-gray",
  media: "pill-blue",
  alta: "pill-yellow",
  urgente: "pill-red",
};

const DesignBoard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/briefs").then((r) => {
      setItems(r.data.filter((b) => b.type === "diseno"));
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDrop = async (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    await api.patch(`/briefs/${id}`, { status });
    load();
  };

  return (
    <Layout
      title="Tablero de Diseño"
      subtitle="Flujo iterativo: brief → propuestas → feedback → aprobación"
      actions={
        <a href="/briefs?new=1" className="btn-primary flex items-center gap-2" data-testid="new-design-brief">
          <Plus size={14} /> Nuevo brief de diseño
        </a>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <Palette size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin solicitudes de diseño</h3>
          <p className="text-[#666] mt-2 mb-5">Crea briefs profesionales con Aura y se asignarán a la diseñadora.</p>
          <a href="/briefs?new=1" className="btn-primary">Crear primer brief</a>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="design-kanban">
          {COLUMNS.map((col) => {
            const colItems = items.filter((b) => b.status === col.id);
            return (
              <div
                key={col.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, col.id)}
                className="kanban-col"
                data-testid={`design-col-${col.id}`}
              >
                <div className="mb-3 px-1 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#666]">{col.label}</span>
                  <span className="text-xs text-[#666]">{colItems.length}</span>
                </div>
                <div className="space-y-2">
                  {colItems.map((b) => (
                    <div
                      key={b.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", b.id)}
                      onClick={() => setSelected(b)}
                      className="kanban-card"
                      data-testid={`design-item-${b.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`pill ${PRIORITY_COLORS[b.priority]} text-[10px]`}>{b.priority}</span>
                        {(b.comments || []).length > 0 && (
                          <span className="text-[10px] text-[#666] flex items-center gap-0.5">
                            💬 {b.comments.length}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium">{b.title}</div>
                      {b.deliverable_format && <div className="text-[11px] text-[#666] mt-1">{b.deliverable_format}</div>}
                      <div className="flex justify-between items-center mt-2 text-xs text-[#666]">
                        <span>{b.assignee_name || "—"}</span>
                        {b.due_date && <span>{new Date(b.due_date).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && <DesignReviewPanel brief={selected} onClose={() => setSelected(null)} onChange={() => { load(); }} />}
    </Layout>
  );
};

const DesignReviewPanel = ({ brief: initialBrief, onClose, onChange }) => {
  const { user } = useAuth();
  const [brief, setBrief] = useState(initialBrief);
  const [comments, setComments] = useState(initialBrief.comments || []);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isProposal, setIsProposal] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const canApprove = user?.role === "ceo" || user?.role === "director_marketing";
  const isDesigner = user?.role === "disenador";

  const refresh = async () => {
    const r = await api.get(`/briefs/${brief.id}`);
    setBrief(r.data);
    setComments(r.data.comments || []);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [comments]);

  const uploadFile = async (f) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "design");
      fd.append("related_type", "brief");
      fd.append("related_id", brief.id);
      const res = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      // Post a review message with attachment
      await api.post(`/workflow/briefs/${brief.id}/review`, {
        content: text || `Subió: ${f.name}`,
        attachment_url: fileUrl(data.id),
        attachment_type: data.content_type?.startsWith("image/") ? "image" : "file",
        is_proposal: isProposal,
      });
      setText("");
      setIsProposal(false);
      await refresh();
      onChange();
      toast.success(isProposal ? "Propuesta subida" : "Archivo subido");
    } catch (err) {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const sendComment = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/workflow/briefs/${brief.id}/review`, {
        content: text,
        is_proposal: false,
      });
      setText("");
      await refresh();
      onChange();
    } catch (err) {
      toast.error("Error");
    } finally { setSending(false); }
  };

  const decide = async (commentId, status) => {
    try {
      await api.post(`/workflow/briefs/${brief.id}/approve-design`, { comment_id: commentId, status });
      toast.success(status === "aprobado" ? "Propuesta aprobada" : "Cambios solicitados");
      await refresh();
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar brief?")) return;
    await api.delete(`/briefs/${brief.id}`);
    onChange();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-start p-6 border-b border-[#E5E5E5]">
          <div>
            <div className="label-overline">Brief diseño · {brief.status?.replace(/_/g, " ")}</div>
            <h2 className="font-display text-2xl font-medium">{brief.title}</h2>
            <div className="text-sm text-[#666] mt-1">
              {brief.assignee_name && <>Asignado a {brief.assignee_name} · </>}
              {brief.deliverable_format && <>{brief.deliverable_format} · </>}
              {brief.due_date && new Date(brief.due_date).toLocaleDateString("es-MX")}
            </div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] flex-1 overflow-hidden">
          {/* Chat thread */}
          <div className="flex flex-col overflow-hidden border-r border-[#E5E5E5]">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="design-thread">
              {comments.length === 0 ? (
                <div className="text-center py-10 text-sm text-[#666]">
                  Aún no hay comentarios ni propuestas. La diseñadora puede subir propuestas low-res y CEO/Director dan feedback aquí.
                </div>
              ) : comments.map((c) => (
                <div key={c.id || c.created_at} className={`flex gap-3 ${c.author_role === user?.role ? "justify-end" : ""}`}>
                  <div className={`max-w-[80%] rounded-2xl p-3 ${c.is_proposal ? "bg-[#FFF9E6] border border-[#D99D26]/30" : "aura-card-flat"} ${c.approved ? "ring-2 ring-green-400" : c.needs_changes ? "ring-2 ring-red-400" : ""}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-xs font-medium">{c.author_name}</div>
                      <span className="text-[10px] text-[#666]">{new Date(c.created_at).toLocaleString("es-MX")}</span>
                      {c.is_proposal && <span className="pill pill-yellow text-[9px]">Propuesta</span>}
                      {c.approved && <span className="pill pill-green text-[9px] flex items-center gap-0.5"><Check size={9} /> Aprobada</span>}
                      {c.needs_changes && <span className="pill pill-red text-[9px]">Cambios</span>}
                    </div>
                    {c.attachment_url && (
                      c.attachment_type === "image" ? (
                        <img src={c.attachment_url} alt="propuesta" className="rounded-lg max-w-full max-h-64 mb-2 border border-[#E5E5E5]" />
                      ) : (
                        <a href={c.attachment_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#8a5e10] inline-flex items-center gap-1 mb-2">
                          <ImageIcon size={12} /> Ver adjunto
                        </a>
                      )
                    )}
                    {c.content && <div className="text-sm whitespace-pre-wrap">{c.content}</div>}
                    {canApprove && c.is_proposal && !c.approved && !c.needs_changes && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => decide(c.id, "aprobado")} className="btn-primary text-xs py-1 px-2 flex items-center gap-1" data-testid={`approve-${c.id}`}>
                          <Check size={11} /> Aprobar
                        </button>
                        <button onClick={() => decide(c.id, "requiere_cambios")} className="btn-outline text-xs py-1 px-2 flex items-center gap-1" data-testid={`request-changes-${c.id}`}>
                          <AlertCircle size={11} /> Pedir cambios
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E5E5E5] p-4 bg-white">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={isProposal ? "Notas para esta propuesta (opcional)..." : "Escribe un comentario o feedback..."}
                rows="2"
                className="aura-input mb-2"
                data-testid="design-message-input"
              />
              <div className="flex justify-between items-center gap-2">
                <div className="flex gap-2 items-center">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadFile(f);
                      e.target.value = "";
                    }}
                  />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline text-xs flex items-center gap-1" data-testid="upload-attachment">
                    {uploading ? <span className="spinner" /> : <Upload size={12} />} Subir {isProposal ? "propuesta" : "archivo"}
                  </button>
                  {(isDesigner || canApprove) && (
                    <label className="text-xs flex items-center gap-1 text-[#666]">
                      <input type="checkbox" checked={isProposal} onChange={(e) => setIsProposal(e.target.checked)} className="accent-[#D99D26]" data-testid="is-proposal-checkbox" />
                      Es propuesta para revisión
                    </label>
                  )}
                </div>
                <button onClick={sendComment} disabled={sending || !text.trim()} className="btn-primary text-xs flex items-center gap-1" data-testid="send-comment">
                  {sending ? <span className="spinner" /> : <Send size={12} />} Comentar
                </button>
              </div>
            </div>
          </div>

          {/* Right side - brief content */}
          <div className="overflow-y-auto p-5 bg-[#FAFAFA]">
            <div className="label-overline mb-2">Brief original</div>
            <div className="text-sm">
              <Markdown content={brief.ai_brief || "(sin contenido)"} />
            </div>
            <div className="border-t border-[#E5E5E5] mt-5 pt-5">
              <button onClick={remove} className="btn-ghost text-red-600 text-xs">
                <Trash2 size={12} className="inline mr-1" /> Eliminar brief
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignBoard;
